import { MessageSquare, TrendingUp, AlertTriangle, Quote } from 'lucide-react';
import { Button } from '@/components/ui/button';
import useTranslation from '@/hooks/useTranslation';
import useResponsesPanelStore from '@/store/ResponsesPanelStore';

interface QuestionStatsBarProps {
  questionId: string;
  responseCount?: number;
  patternCount?: number;
  divergentCount?: number;
  quoteCount?: number;
}

function Chip({ icon, label, value }: { icon: React.ReactNode; label: string; value: number }) {
  return (
    <div className="flex items-center gap-1.5 text-xs">
      {icon}
      <span className="text-neutral-500">{label}</span>
      <span className="font-semibold text-neutral-900">{value}</span>
    </div>
  );
}

/**
 * Compact, mobile-first stats bar rendered above the main Questions
 * content below the lg breakpoint (< 1024px). Mirrors the top section
 * of QuestionSidebar (response / patterns / divergent views / quote
 * counts) so researchers on narrow screens still see the essential
 * question metrics, and gives them a "View responses" button that
 * opens the ResponsesPanel via useResponsesPanelStore.open.
 *
 * Categories are intentionally NOT shown here — they already appear in
 * the DataZone inside the main content, so re-surfacing them would
 * duplicate information and add visual noise on small screens.
 *
 * At lg+ the component is hidden via `lg:hidden`, and the full
 * QuestionSidebar takes over on the right.
 */
function QuestionStatsBar({
  questionId,
  responseCount,
  patternCount,
  divergentCount,
  quoteCount,
}: QuestionStatsBarProps) {
  const { lang } = useTranslation();
  const { open } = useResponsesPanelStore();

  // Nothing to render if there are no stats at all — avoids an empty bar.
  const hasAnyStat =
    (responseCount ?? 0) > 0 ||
    (patternCount ?? 0) > 0 ||
    (divergentCount ?? 0) > 0 ||
    (quoteCount ?? 0) > 0;

  if (!hasAnyStat) return null;

  return (
    <div className="lg:hidden flex items-center justify-between gap-3 mb-4 px-4 py-3 rounded-xl border border-neutral-200/80 bg-white shadow-sm">
      <div className="flex items-center gap-3 flex-wrap min-w-0">
        {responseCount !== undefined && responseCount > 0 && (
          <Chip
            icon={<MessageSquare className="h-3.5 w-3.5 text-primary" />}
            label={lang.get('msg.responses')}
            value={responseCount}
          />
        )}
        {patternCount !== undefined && patternCount > 0 && (
          <Chip
            icon={<TrendingUp className="h-3.5 w-3.5 text-primary" />}
            label={lang.get('msg.patternsFound')}
            value={patternCount}
          />
        )}
        {divergentCount !== undefined && divergentCount > 0 && (
          <Chip
            icon={<AlertTriangle className="h-3.5 w-3.5 text-chat-primary" />}
            label={lang.get('msg.divergentViews')}
            value={divergentCount}
          />
        )}
        {quoteCount !== undefined && quoteCount > 0 && (
          <Chip
            icon={<Quote className="h-3.5 w-3.5 text-chat-primary" />}
            label={lang.get('msg.quotes')}
            value={quoteCount}
          />
        )}
      </div>
      <Button variant="outline" size="sm" className="shrink-0" onClick={() => open(questionId)}>
        <MessageSquare className="h-3.5 w-3.5 mr-1.5" />
        {lang.get('msg.viewResponses')}
      </Button>
    </div>
  );
}

export default QuestionStatsBar;
